import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { listBlogComments, addBlogComment } from "@/lib/blog-comments.functions";
import { useAuth } from "@/hooks/use-auth";

export function BlogComments({ postId }: { postId: string }) {
  const list = useServerFn(listBlogComments);
  const add = useServerFn(addBlogComment);
  const qc = useQueryClient();
  const { signedIn } = useAuth();
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["blog-comments", postId],
    queryFn: () => list({ data: { post_id: postId } }),
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (body.trim().length < 2) return;
    setPosting(true);
    try {
      await add({ data: { post_id: postId, body: body.trim() } });
      setBody("");
      qc.invalidateQueries({ queryKey: ["blog-comments", postId] });
      toast.success("Kommentar publicerad");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte publicera");
    } finally {
      setPosting(false);
    }
  }

  return (
    <section className="mt-16 border-t border-border pt-10">
      <h2 className="flex items-center gap-2 font-display text-2xl">
        <MessageCircle size={20} /> Kommentarer
      </h2>

      <ul className="mt-6 space-y-4">
        {isLoading && <p className="text-sm text-muted-foreground">Laddar…</p>}
        {!isLoading && (data?.comments ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">Inga kommentarer ännu. Bli den första!</p>
        )}
        {(data?.comments ?? []).map((c: any) => (
          <li key={c.id} className="rounded-2xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">
              {c.author?.display_name || "Medlem"} · {new Date(c.created_at).toLocaleDateString("sv-SE")}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/90">{c.body}</p>
          </li>
        ))}
      </ul>

      {signedIn ? (
        <form onSubmit={submit} className="mt-6 space-y-3">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Din kommentar…"
            className="w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
          <button disabled={posting || body.trim().length < 2}
            className="rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground disabled:opacity-60">
            {posting ? "Publicerar…" : "Kommentera"}
          </button>
        </form>
      ) : (
        <p className="mt-6 rounded-2xl border border-border bg-card p-4 text-sm">
          <Link to="/login" className="text-accent hover:underline">Logga in</Link> för att kommentera.
        </p>
      )}
    </section>
  );
}
